import type { ListJobsResponse } from '@vy/api-definition';
import { JOB_TASK_STATUSES, JOB_TYPES } from '@vy/jobs-types';
import { ExtractContractFromPdfTaskMetadataSchema } from '@vy/contracts-types';
import { isNotEmptyArray, isNullOrUndefined } from '@vy/utils';
import { useJobs } from '../../../../hooks/requests/use-jobs';

type JobTask = ListJobsResponse[number]['tasks'][number];

type ActiveExtractionTask = JobTask & {
  jobId: string;
  filename: string;
};

const POLLING_INTERVAL_MS = 3_000;
const UNKNOWN_FILENAME = 'Unknown file';
const ACTIVE_TASK_STATUSES = [
  JOB_TASK_STATUSES.Pending,
  JOB_TASK_STATUSES.InProgress,
  JOB_TASK_STATUSES.Blocked,
] as const;

export function useActiveExtractionTasks() {
  const { data: jobs, refetch: refetchJobs } = useJobs({
    refetchInterval: (data) => {
      const shouldPoll = hasActiveExtractionJobs(data);
      return shouldPoll ? POLLING_INTERVAL_MS : false;
    },
  });

  const extractionJobs = (jobs ?? []).filter(
    (job) => job.type === JOB_TYPES.ExtractContractFromPdf,
  );

  const activeExtractionTasks = extractionJobs.flatMap((job) =>
    job.tasks
      .filter(filterActiveExtractionTasks)
      .map((task) => buildActiveTask(task, job.id)),
  );

  return {
    activeExtractionTasks,
    hasActiveTasks: isNotEmptyArray(activeExtractionTasks),
    refetchJobs,
  };
}

function filterActiveExtractionTasks(task: JobTask): boolean {
  return (ACTIVE_TASK_STATUSES as readonly string[]).includes(task.status);
}

function hasActiveExtractionJobs(jobs: ListJobsResponse | undefined): boolean {
  if (isNullOrUndefined(jobs)) {
    return false;
  }

  return jobs.some((job) => {
    const isExtractionJob = job.type === JOB_TYPES.ExtractContractFromPdf;
    const hasActiveTasks = job.tasks.some(filterActiveExtractionTasks);

    return isExtractionJob && hasActiveTasks;
  });
}

function buildActiveTask(task: JobTask, jobId: string): ActiveExtractionTask {
  const metadataParseResult = ExtractContractFromPdfTaskMetadataSchema.safeParse(task.metadata);
  const filename = metadataParseResult.success ? metadataParseResult.data.fileName : UNKNOWN_FILENAME;

  return {
    ...task,
    jobId,
    filename,
  };
}
